// client/src/components/about/AboutPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box, Button, Card, CardContent,
  Container, Grid, Link, Skeleton, Typography
} from '@mui/material';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

const DEFAULTS = {
  aboutHeadline: 'Track Everything You Collect',
  aboutTagline: 'One place for all your collections',
  aboutDescription: '',
  aboutFeatures: [],
  aboutCtaText: 'Get Started Free',
  metaDescription: 'InterCollect is a self-hosted collection manager for games, movies, books, music, LEGO and more.',
};

export default function AboutPage() {
  const navigate = useNavigate();
  const { featureFlags, loading: flagsLoading } = useFeatureFlags();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance.get('/seo/public')
      .then(res => setContent(res.data))
      .catch(() => setContent(DEFAULTS))
      .finally(() => setLoading(false));
  }, []);

  // Redirect to login when flags loaded and discovery is off
  useEffect(() => {
    if (!flagsLoading && !featureFlags.enableDiscovery) {
      navigate('/login', { replace: true });
    }
  }, [flagsLoading, featureFlags.enableDiscovery, navigate]);

  if (loading || flagsLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Skeleton variant="text" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="text" height={40} sx={{ mb: 4 }} />
        <Skeleton variant="rectangular" height={200} />
      </Container>
    );
  }

  const c = { ...DEFAULTS, ...content };

  return (
    <>
      <Helmet>
        <title>What is InterCollect? — InterCollect</title>
        <meta name="description" content={c.metaDescription} />
        <meta property="og:title" content="What is InterCollect?" />
        <meta property="og:description" content={c.metaDescription} />
        <meta property="og:type" content="website" />
      </Helmet>

      <Container maxWidth="md" sx={{ py: 8 }}>
        {/* Hero */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            {c.aboutHeadline}
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {c.aboutTagline}
          </Typography>
          <Button
            component={RouterLink}
            to="/download"
            variant="contained"
            size="large"
            sx={{ mt: 3 }}
          >
            {c.aboutCtaText}
          </Button>
        </Box>

        {/* Description */}
        {c.aboutDescription && (
          <Typography variant="body1" sx={{ mb: 6, textAlign: 'center' }}>
            {c.aboutDescription}
          </Typography>
        )}

        {/* Feature cards */}
        {c.aboutFeatures.length > 0 && (
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {c.aboutFeatures.map((feature, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>{feature.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Footer */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2">
            Already have an account?{' '}
            <Link component={RouterLink} to="/login">Sign In</Link>
          </Typography>
        </Box>
      </Container>
    </>
  );
}
